import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from './config';
import { authRefreshToken, authSsoInit } from './auth';
import {
  buildUserFromResponse,
  getStoredUser,
  persistUserToStorage,
  clearStoredUser,
} from '@utils/userSession';
import {
  extractCognitoUsername,
  setSessionCookie,
} from '@utils/sessionCookie';
import { ssoLoginPath } from '@utils/ssoSession';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Keep a 403 from tearing down the session. Set it on calls where "you may
     * not do this" is a routine answer the caller handles itself (a feature
     * gate that is off, a process that stopped being an editable draft) rather
     * than evidence that the login is dead.
     */
    skipAuthRedirect?: boolean;
  }
}

const TIMEOUT = 600_000;

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to attach JWT token
client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const storedUser = getStoredUser();
    if (storedUser?.token) {
      config.headers.Authorization = `Bearer ${storedUser.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if a refresh is already in progress to avoid multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Attempt to refresh the current auth token. Deduplicates concurrent calls so
 * only one refresh request is in-flight at a time. Returns the new access token
 * on success, or `null` when refresh is impossible (missing credentials, etc.).
 *
 * Shared by the Axios 401 interceptor and raw-fetch paths (SSE, reconnect).
 */
export async function refreshAuthToken(): Promise<string | null> {
  const userData = getStoredUser();
  if (!userData?.refreshToken) return null;

  const cognitoUsername = extractCognitoUsername(userData.token);
  if (!cognitoUsername) return null;

  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = authRefreshToken(userData.refreshToken, cognitoUsername, userData.email)
      .then(async (data) => {
        let finalToken = data.accessToken;

        try {
          const profile = await authSsoInit(data.accessToken);
          if (profile.userId && profile.email && profile.accessToken) {
            finalToken = profile.accessToken;
            const refreshed = buildUserFromResponse(profile, {
              pendingInviteToken: userData.pendingInviteToken,
              refreshToken: profile.refreshToken ?? userData.refreshToken ?? null,
            });
            persistUserToStorage(refreshed);
          } else {
            persistUserToStorage({ ...userData, token: finalToken });
          }
        } catch {
          persistUserToStorage({ ...userData, token: finalToken });
        }

        setSessionCookie(finalToken);
        return finalToken;
      })
      .catch(() => null)
      .finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

// Response interceptor with token refresh on 401
client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // On 401, attempt token refresh (once)
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !(originalRequest as unknown as Record<string, unknown>)._retried
    ) {
      (originalRequest as unknown as Record<string, unknown>)._retried = true;

      const newToken = await refreshAuthToken();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return client(originalRequest);
      }
      redirectToLogin();
    }

    // On 403, clear auth
    if (error.response?.status === 403 && !originalRequest?.skipAuthRedirect) {
      clearStoredUser();
      redirectToLogin();
    }

    throw error;
  }
);

function redirectToLogin() {
  const ssoProvider = getStoredUser()?.ssoProvider;
  clearStoredUser();
  const path = globalThis.location.pathname;
  if (path.startsWith('/auth/') || path.startsWith('/login') || path.startsWith('/sso/')) return;
  globalThis.location.href = ssoProvider ? ssoLoginPath(ssoProvider) : '/auth/login';
}

export default client;

